"use client";

import ModalCloseIcon from "@/assets/icons/modal-close.svg";
import TextLogoIcon from "@/assets/images/logo-only-vidmuse.svg";
import LogoIcon from "@/assets/images/logo-with-vidmuse.svg";
import Image from "next/image";
import Link from "next/link";
import { memo, useEffect, useRef, useState } from "react";

interface ModalHeaderProps {
  size?: "small" | "large";
  onClose: () => void;
}

export const ModalHeader = memo(function ModalHeader(props: ModalHeaderProps) {
  const { onClose, size = "small" } = props;
  const [isScrolled, setIsScrolled] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let isAnimationFrameQueued = false;
    let rafId: number | null = null;
    const headerElement = headerRef.current;

    function getScrollContainer(
      element: HTMLElement | null,
    ): HTMLElement | Window {
      let parent = element?.parentElement;
      while (parent) {
        const style = window.getComputedStyle(parent);
        const overflow = style.overflowY;
        if (
          (overflow === "auto" || overflow === "scroll") &&
          parent.scrollHeight > parent.clientHeight
        ) {
          return parent;
        }
        // 特殊处理 SimpleBar
        if (parent.classList.contains("simplebar-content-wrapper")) {
          return parent;
        }
        parent = parent.parentElement;
      }
      return window;
    }

    const scrollContainer = getScrollContainer(headerElement);
    const isInModal = !!headerElement?.closest('[role="dialog"]');

    function updateScrolledState() {
      let scrollY = 0;
      if (scrollContainer === window) {
        // 如果在弹窗中，忽略 window 的滚动
        scrollY = isInModal ? 0 : window.scrollY;
      } else {
        scrollY = (scrollContainer as HTMLElement).scrollTop;
      }

      const hasScrolled = scrollY > 0;
      setIsScrolled(hasScrolled);
    }

    function onScroll() {
      if (!isAnimationFrameQueued) {
        rafId = requestAnimationFrame(() => {
          updateScrolledState();
          isAnimationFrameQueued = false;
          rafId = null;
        });
        isAnimationFrameQueued = true;
      }
    }

    // 初始检查
    updateScrolledState();

    // 监听滚动
    scrollContainer.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      scrollContainer.removeEventListener("scroll", onScroll);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);

  return (
    <div
      ref={headerRef}
      className={`sticky top-0 transition-all duration-300 w-full z-50 mx-auto flex ${
        size === "small" ? "h-[60px]" : "h-[96px]"
      } items-center justify-between px-5 lg:pl-8 lg:pr-[60px] ${
        isScrolled
          ? "bg-white/38 backdrop-blur-[11px] shadow-[0_3.45px_29.785px_0_rgba(0,123,255,0.03)]"
          : "bg-transparent"
      }`}
    >
      <Link
        href="/"
        onClick={(e) => {
          e.preventDefault();
          onClose();
        }}
      >
        <div className="flex items-center gap-2">
          {size === "large" ? (
            <>
              <Image src="/logo.png" alt="VidMuse" width={26} height={24} />
              <TextLogoIcon
                className="text-text-primary w-[91px]"
                alt="VidMuse"
              />
            </>
          ) : (
            <LogoIcon className="text-text-primary w-[128px]" alt="VidMuse" />
          )}
        </div>
      </Link>

      <button
        type="button"
        className="cursor-pointer border-0 bg-transparent p-0"
        onClick={onClose}
        aria-label="Close modal"
      >
        <ModalCloseIcon className="h-5 w-5 text-text-primary" />
      </button>
    </div>
  );
});
